import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { List, X, SignOut } from "@phosphor-icons/react";

const memberLinks = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/training", label: "Training" },
  { to: "/downloads", label: "Downloads" },
  { to: "/pricing", label: "Pricing" },
  { to: "/inventory", label: "Inventory" },
  { to: "/support", label: "Support" },
];

export function Navbar() {
  const { user, isAuthenticated, isAdmin, isMember, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = isAuthenticated ? memberLinks : [
    { to: "/", label: "Home" },
    { to: "/pricing", label: "Pricing" },
    { to: "/inventory", label: "Inventory" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-[#262626]" data-testid="main-navbar">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
        <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="navbar-logo">
          <div className="w-9 h-9 bg-[#00D4FF] flex items-center justify-center" aria-hidden>
            <span className="text-black font-chivo font-black text-lg leading-none">D</span>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-chivo font-black text-white text-base tracking-tight uppercase">Dry Ice</span>
            <span className="font-mono text-[10px] text-[#00D4FF] tracking-[0.25em]">CONNECT</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav-links">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-chivo font-bold uppercase tracking-wider transition-colors ${
                  isActive ? "text-[#00D4FF]" : "text-white hover:text-[#00D4FF]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-link-admin"
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-chivo font-bold uppercase tracking-wider border border-[#FF5722] ml-2 ${
                  isActive ? "bg-[#FF5722] text-black" : "text-[#FF5722] hover:bg-[#FF5722] hover:text-black"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {!isAuthenticated && (
            <>
              <Link to="/login" className="btn-secondary text-xs py-2 px-4" data-testid="navbar-login-btn">Login</Link>
              <Link to="/register" className="btn-primary text-xs py-2 px-4" data-testid="navbar-register-btn">Join Now</Link>
            </>
          )}
          {isAuthenticated && (
            <>
              {!isMember && (
                <Link to="/billing" className="btn-primary text-xs py-2 px-4" data-testid="navbar-activate-btn">Activate</Link>
              )}
              <Link to="/account" className="text-sm font-mono text-[#9CA3AF] hover:text-white" data-testid="navbar-account-link">
                {user?.name || user?.email}
              </Link>
              <button onClick={handleLogout} className="btn-secondary text-xs py-2 px-4" data-testid="navbar-logout-btn">
                <SignOut size={14} weight="bold" /> Logout
              </button>
            </>
          )}
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#262626] bg-[#0A0A0A]" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-chivo font-bold uppercase tracking-wider border-l-2 ${
                    isActive ? "text-[#00D4FF] border-[#00D4FF]" : "text-white border-transparent"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className="px-3 py-3 text-sm font-chivo font-bold uppercase text-[#FF5722]">
                Admin
              </NavLink>
            )}
            <div className="border-t border-[#262626] mt-2 pt-3 flex flex-col gap-2">
              {!isAuthenticated ? (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary text-xs">Login</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-xs">Join Now</Link>
                </>
              ) : (
                <>
                  <Link to="/account" onClick={() => setOpen(false)} className="btn-secondary text-xs">Account</Link>
                  <button onClick={handleLogout} className="btn-secondary text-xs">Logout</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
